// Pricing Table AI Vibe Coded - React Component
// Add this component to your React app

import React, { useEffect, useRef } from 'react';

const PricingTable = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        // Configuration - Updated to use localhost database
        const config = {
            apiKey: '4mDPjcb6EK9OYrdtwOQtUafatTRUWcEw',
            lambdaEndpoint: 'http://localhost:3000/api/install/deliver',
            trackingEndpoint: 'http://localhost:3000/api/analytics',
            containerId: 'pricing-table-container'
            // API will automatically find user's active table
        };
        
        console.log('🔧 Pricing Table Config:', config);
        
        // Enhanced tracking class from installCodeGenerator.ts
        class PricingTableTracker {
            config: any;
            sessionId: any;
            pageLoadTime: any;
            tableVisibleTime: any;
            totalPageDuration: any;
            tableVisibleDuration: any;
            lastActivityTime: any;
            isTracking: any;
            updateTimer: any;
            heartbeatTimer: any;
            observer: any;
            pricingTableData: any;
            
            constructor(config: any) {
                this.config = {
                    apiKey: config.apiKey,
                    lambdaEndpoint: config.lambdaEndpoint || 'http://localhost:3000',
                    trackingEndpoint: config.trackingEndpoint || 'http://localhost:3000',
                    updateInterval: config.updateInterval || 5000,
                    sessionId: null,
                    pageLoadTime: Date.now(),
                    tableVisibleTime: null,
                    totalPageDuration: 0,
                    tableVisibleDuration: 0,
                    lastActivityTime: Date.now(),
                    isTracking: false,
                    updateTimer: null,
                    heartbeatTimer: null,
                    observer: null,
                    ...config
                };
                this.init();
            }
            
            init() {
                this.startTracking();
                this.setupVisibilityTracking();
                this.setupTableVisibilityTracking();
                this.startPeriodicUpdates();
                this.startHeartbeat();
                this.setupSessionEnd();
            }
            
            startTracking() {
                if(this.isTracking) return;
                this.isTracking = true;
                this.pageLoadTime = Date.now();
                this.lastActivityTime = Date.now();
                
                if(this.config.sessionId) {
                    this.sessionId = this.config.sessionId;
                    this.logInteraction('session_create', {
                        sessionId: this.sessionId,
                        pageLoadTime: this.pageLoadTime
                    });
                }
            }
            
            setupVisibilityTracking() {
                document.addEventListener('visibilitychange', () => {
                    document.hidden ? this.pauseTracking() : this.resumeTracking();
                });
                ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
                    document.addEventListener(event, () => {
                        this.lastActivityTime = Date.now();
                    }, {passive: true});
                });
            }
            
            setupTableVisibilityTracking() {
                const tableElement = this.findPricingTable();
                if(!tableElement) return;
                this.observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        entry.isIntersecting ? this.onTableVisible() : this.onTableHidden();
                    });
                }, {threshold: 0.1});
                this.observer.observe(tableElement);
            }
            
            findPricingTable() {
                const selectors = ['[data-pricing-table]', '.pricing-table', '.pricing-table-container', '#pricing-table', '[class*="pricing"]', '[class*="price"]'];
                for(const selector of selectors) {
                    const element = document.querySelector(selector);
                    if(element) return element;
                }
                const elements = document.querySelectorAll('*');
                for(const element of elements) {
                    const text = element.textContent?.toLowerCase() || '';
                    if(text.includes('pricing') || text.includes('price') || text.includes('plan')) {
                        return element;
                    }
                }
                return null;
            }
            
            onTableVisible() {
                if(!this.tableVisibleTime) {
                    this.tableVisibleTime = Date.now();
                    this.logInteraction('first_view', {
                        timeToFirstView: this.tableVisibleTime - this.pageLoadTime
                    });
                }
            }
            
            onTableHidden() {}
            
            startPeriodicUpdates() {
                this.updateTimer = setInterval(() => {
                    this.updateSession();
                }, this.config.updateInterval);
            }
            
            startHeartbeat() {
                this.heartbeatTimer = setInterval(() => {
                    this.sendHeartbeat();
                }, 3000);
            }
            
            sendHeartbeat() {
                if(!this.sessionId || !this.isTracking) return;
                const now = Date.now();
                const duration = Math.floor((now - this.pageLoadTime) / 1000);
                
                // Send heartbeat event
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'heartbeat',
                    duration_seconds: duration
                });
                
                // Update session duration every 3 seconds (0, 3, 6, 9, etc.) up to 180
                const durationIncrements = Math.min(Math.floor(duration / 3) * 3, 180);
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    duration_seconds: durationIncrements
                });
            }
            
            updateSession() {
                if(!this.sessionId || !this.isTracking) return;
                const now = Date.now();
                this.totalPageDuration = Math.floor((now - this.pageLoadTime) / 1000);
                if(this.tableVisibleTime) {
                    this.tableVisibleDuration = Math.floor((now - this.tableVisibleTime) / 1000);
                }
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    pricingTableId: 1,
                    duration_seconds: this.totalPageDuration,
                    tableVisibleDuration: this.tableVisibleDuration,
                    lastActivityTime: new Date(this.lastActivityTime).toISOString()
                });
            }
            
            pauseTracking() {
                this.isTracking = false;
            }
            
            resumeTracking() {
                this.isTracking = true;
                this.lastActivityTime = Date.now();
            }
            
            logInteraction(interactionType: any, interactionData: any = {}) {
                if(!this.sessionId) return;
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'interaction',
                    interactionType: interactionType,
                    interactionData: interactionData,
                    timestamp: new Date().toISOString()
                });
            }
            
            trackBillingPeriodView(billingPeriod: any) {
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'billing_period_view',
                    eventData: {period: billingPeriod}
                });
                
                // Update session with billing period viewed status (only if currently NV)
                const billingPeriodLower = billingPeriod.toLowerCase();
                const updateData: any = {};
                
                // Only update if the billing period is currently "NV" (not viewed)
                // We'll let the backend handle the logic to prevent overwriting ST or NI
                if (billingPeriodLower.includes('monthly')) {
                    updateData.monthlyBillingPeriodViewed = 'VW';
                }
                if (billingPeriodLower.includes('quarterly')) {
                    updateData.quarterlyBillingPeriodViewed = 'VW';
                }
                if (billingPeriodLower.includes('annual') || billingPeriodLower.includes('yearly')) {
                    updateData.annuallyBillingPeriodViewed = 'VW';
                }
                if (billingPeriodLower.includes('lifetime') || billingPeriodLower.includes('one-time')) {
                    updateData.lifetimeBillingPeriodViewed = 'VW';
                }
                
                if (Object.keys(updateData).length > 0) {
                    this.sendTrackingRequest('/session/update', {
                        sessionId: this.sessionId,
                        apiKey: this.config.apiKey,
                        ...updateData
                    });
                }
            }
            
            trackBillingPeriodClick(billingPeriod: any) {
                this.logInteraction('billing_period_click', {billingPeriod});
            }
            
            trackConversion(tier: any, billingPeriod: any) {
                this.logInteraction('conversion', {tier, billingPeriod});
            }
            
            trackExternalLinkClick(url: any) {
                this.logInteraction('external_link_click', {url});
                
                // Update session with external_link_clicked
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    externalLinkClicked: true
                });
            }
            
            trackTableClick() {
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'table_click'
                });
                
                // Update session with table_clicked
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    tableClicked: true
                });
            }
            
            trackTableHover() {
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'table_hover'
                });
                
                // Update session with table_hovered
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    tableHovered: true
                });
            }
            
            trackCTAClick(linkUrl: any, tier: any, billingPeriod: any) {
                this.sendTrackingRequest('/event', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    eventType: 'cta_click',
                    eventData: linkUrl ? {linkUrl: linkUrl, tier: tier, billingPeriod: billingPeriod} : undefined
                });
                
                // Update session with cta_clicked and conversion data
                this.sendTrackingRequest('/session/update', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey,
                    ctaClicked: true,
                    conversionTier: tier,
                    conversionBillingPeriod: billingPeriod,
                    conversionLink: linkUrl
                });
            }
            
            setupTableEventListeners() {
                const tableElement = this.findPricingTable();
                if(!tableElement) return;
                this.setupBillingPeriodTracking(tableElement);
                this.setupConversionTracking(tableElement);
                this.setupExternalLinkTracking(tableElement);
                this.setupGeneralClickTracking(tableElement);
                this.setupTableHoverTracking(tableElement);
                this.setupCTATracking(tableElement);
            }
            
            setupBillingPeriodTracking(tableElement: any) {
                const billingSelectors = ['[data-billing-period]', '[class*="billing"]', '[class*="period"]', 'input[type="radio"]', 'input[type="checkbox"]'];
                billingSelectors.forEach(selector => {
                    const elements = tableElement.querySelectorAll(selector);
                    elements.forEach((element: any) => {
                        const observer = new IntersectionObserver(entries => {
                            entries.forEach(entry => {
                                if(entry.isIntersecting) {
                                    const billingPeriod = this.extractBillingPeriod(element);
                                    if(billingPeriod) {
                                        this.trackBillingPeriodView(billingPeriod);
                                    }
                                }
                            });
                        });
                        observer.observe(element);
                        element.addEventListener('click', (e: any) => {
                            const billingPeriod = this.extractBillingPeriod(element);
                            if(billingPeriod) {
                                this.trackBillingPeriodClick(billingPeriod);
                            }
                        });
                    });
                });
                
                // Track price visibility using Intersection Observer
                this.setupPriceVisibilityTracking(tableElement);
            }
            
            setupConversionTracking(tableElement: any) {
                const conversionSelectors = ['[data-conversion]', '[class*="buy"]', '[class*="subscribe"]', '[class*="purchase"]', 'button[class*="cta"]', 'a[class*="cta"]'];
                conversionSelectors.forEach(selector => {
                    const elements = tableElement.querySelectorAll(selector);
                    elements.forEach((element: any) => {
                        element.addEventListener('click', (e: any) => {
                            const tier = this.extractTier(element);
                            const billingPeriod = this.extractBillingPeriod(element);
                            this.trackConversion(tier, billingPeriod);
                        });
                    });
                });
            }
            
            setupExternalLinkTracking(tableElement: any) {
                const links = tableElement.querySelectorAll('a[href]');
                links.forEach((link: any) => {
                    const href = link.getAttribute('href');
                    if(href && !href.startsWith('#') && !href.startsWith('/')) {
                        link.addEventListener('click', (e: any) => {
                            this.trackExternalLinkClick(href);
                        });
                    }
                });
            }
            
            setupGeneralClickTracking(tableElement: any) {
                tableElement.addEventListener('click', (e: any) => {
                    this.trackTableClick();
                });
            }
            
            setupTableHoverTracking(tableElement: any) {
                tableElement.addEventListener('mouseenter', (e: any) => {
                    this.trackTableHover();
                });
            }
            
            setupCTATracking(tableElement: any) {
                const allLinks = tableElement.querySelectorAll('a[href]');
                const ctaTextPatterns = ['buy', 'subscribe', 'get started', 'sign up', 'purchase', 'join', 'start', 'choose', 'select'];
                
                allLinks.forEach((link: any) => {
                    const href = link.getAttribute('href');
                    const text = link.textContent?.toLowerCase().trim();
                    if(href) {
                        const isExternalLink = href && !href.startsWith('#') && !href.startsWith('/') && href !== 'javascript:void(0)';
                        const hasCTAText = ctaTextPatterns.some(pattern => text?.includes(pattern));
                        if(isExternalLink || hasCTAText) {
                            // Find the tier and billing period for this link from the template data
                            const linkData = this.findLinkDataFromTemplate(href);
                            console.log('🔗 Found CTA link:', {href, text, tier: linkData?.tier, billingPeriod: linkData?.billingPeriod});
                            
                            // Store the link data on the element itself for quick access
                            (link as any)._linkData = linkData;
                            
                            link.addEventListener('click', (e: any) => {
                                // Prevent default navigation temporarily
                                e.preventDefault();
                                
                                const linkData = (link as any)._linkData;
                                console.log('🔗 CTA link clicked - sending data before navigation:', {href, tier: linkData?.tier, billingPeriod: linkData?.billingPeriod});
                                
                                // Send tracking data immediately
                                this.trackCTAClick(href, linkData?.tier, linkData?.billingPeriod);
                                
                                // Store in sessionStorage for debugging
                                sessionStorage.setItem('last_cta_click', JSON.stringify({
                                    href,
                                    tier: linkData?.tier,
                                    billingPeriod: linkData?.billingPeriod,
                                    timestamp: new Date().toISOString()
                                }));
                                
                                // Navigate after a short delay to ensure tracking is sent
                                setTimeout(() => {
                                    console.log('🔗 Navigating to:', href);
                                    window.location.href = href;
                                }, 200);
                            });
                        }
                    }
                });
            }
            
            findLinkDataFromTemplate(linkUrl: string): {tier: string, billingPeriod: string} | null {
                console.log('🔍 Looking for link data:', linkUrl);
                console.log('📊 Available pricing data:', this.pricingTableData);
                
                // First, try to extract from the DOM since we don't have variation_data
                console.log('🔍 Trying DOM-based extraction...');
                const domData = this.extractFromDOM(linkUrl);
                if (domData) {
                    console.log('✅ DOM extraction successful:', domData);
                    return domData;
                }
                
                // Get the pricing table data from the template
                if (this.pricingTableData && this.pricingTableData.variation_data) {
                    const variationData = this.pricingTableData.variation_data;
                    console.log('📋 Variation data:', variationData);
                    
                    // Check if we have filled fields with link data
                    if (variationData.filled_fields) {
                        console.log('🔗 Checking filled fields for link match...');
                        for (const field of variationData.filled_fields) {
                            console.log('📝 Field:', field);
                            if (field.field_type === 'link') {
                                console.log('🔗 Comparing:', field.value, 'with', linkUrl);
                                // Try exact match first
                                if (field.value === linkUrl) {
                                    console.log('✅ Exact match found!');
                                    const tier = this.findTierForField(field);
                                    const billingPeriod = this.findBillingPeriodForField(field);
                                    return { tier, billingPeriod };
                                }
                                // Try partial match (link contains the field value or vice versa)
                                if (field.value && (linkUrl.includes(field.value) || field.value.includes(linkUrl))) {
                                    console.log('✅ Partial match found!');
                                    const tier = this.findTierForField(field);
                                    const billingPeriod = this.findBillingPeriodForField(field);
                                    return { tier, billingPeriod };
                                }
                            }
                        }
                    }
                    
                    // Check tierPricing structure for link matches
                    if (variationData.tierPricing) {
                        console.log('💰 Checking tierPricing for link match...');
                        for (const [tierKey, tierData] of Object.entries(variationData.tierPricing)) {
                            if (tierData && typeof tierData === 'object' && 'billingPeriods' in tierData) {
                                const billingPeriods = (tierData as any).billingPeriods;
                                if (billingPeriods && typeof billingPeriods === 'object') {
                                    for (const [billingPeriod, priceArray] of Object.entries(billingPeriods)) {
                                        if (Array.isArray(priceArray)) {
                                            for (const priceObj of priceArray) {
                                                if (priceObj && typeof priceObj === 'object' && 'link' in priceObj && priceObj.link === linkUrl) {
                                                    console.log('✅ Found match in tierPricing:', { tierKey, billingPeriod });
                                                    return { 
                                                        tier: tierKey, 
                                                        billingPeriod: billingPeriod 
                                                    };
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    // Fallback: check if we have detected prices with links
                    if (variationData.detectedPrices) {
                        console.log('💰 Checking detected prices for link match...');
                        for (const priceData of variationData.detectedPrices) {
                            console.log('💰 Price data:', priceData);
                            if (priceData.link && (priceData.link === linkUrl || linkUrl.includes(priceData.link) || priceData.link.includes(linkUrl))) {
                                console.log('✅ Price link match found!');
                                return { 
                                    tier: priceData.tier || 'unknown', 
                                    billingPeriod: priceData.billingPeriod || 'monthly' 
                                };
                            }
                        }
                    }
                }
                
                console.log('❌ No link data found, returning null');
                return null;
            }
            
            extractFromDOM(linkUrl: string): {tier: string, billingPeriod: string} | null {
                // Find the link element in the DOM
                const linkElement = document.querySelector(`a[href="${linkUrl}"]`) || 
                                   document.querySelector(`a[href*="${linkUrl.split('/').pop()}"]`);
                
                if (linkElement) {
                    console.log('🔍 Found link element in DOM:', linkElement);
                    
                    // Find the closest pricing tier container
                    const tierContainer = linkElement.closest('.pricing-tier') || 
                                        linkElement.closest('[class*="tier"]') ||
                                        linkElement.closest('[class*="pricing"]');
                    
                    if (tierContainer) {
                        console.log('🏷️ Found tier container:', tierContainer);
                        
                        // Extract tier name from the container
                        const tierNameElement = tierContainer.querySelector('.tier-name') || 
                                              tierContainer.querySelector('h3') ||
                                              tierContainer.querySelector('[class*="name"]');
                        
                        const tier = tierNameElement ? tierNameElement.textContent?.trim().toLowerCase() : 'unknown';
                        
                        // Determine billing period from the current toggle state
                        const billingToggle = document.getElementById('billingToggle') as HTMLInputElement;
                        const billingPeriod = billingToggle && billingToggle.checked ? 'annual' : 'monthly';
                        
                        console.log('📊 DOM extraction result:', { tier, billingPeriod });
                        return { tier, billingPeriod };
                    }
                }
                
                return null;
            }
            
            findTierForField(field: any): string {
                // Look for tier information in the field or its context
                if (field.tier) return field.tier;
                if (field.context && field.context.tier) return field.context.tier;
                
                // Try to extract from field name or description
                const fieldName = field.field_name?.toLowerCase() || '';
                if (fieldName.includes('starter') || fieldName.includes('basic')) return 'starter';
                if (fieldName.includes('pro') || fieldName.includes('professional')) return 'professional';
                if (fieldName.includes('enterprise') || fieldName.includes('premium')) return 'enterprise';
                
                return 'unknown';
            }
            
            findBillingPeriodForField(field: any): string {
                // Look for billing period information in the field or its context
                if (field.billingPeriod) return field.billingPeriod;
                if (field.context && field.context.billingPeriod) return field.context.billingPeriod;
                
                // Try to extract from field name or description
                const fieldName = field.field_name?.toLowerCase() || '';
                const fieldDesc = field.description?.toLowerCase() || '';
                
                if (fieldName.includes('annual') || fieldDesc.includes('annual')) return 'annual';
                if (fieldName.includes('monthly') || fieldDesc.includes('monthly')) return 'monthly';
                if (fieldName.includes('quarterly') || fieldDesc.includes('quarterly')) return 'quarterly';
                if (fieldName.includes('lifetime') || fieldDesc.includes('lifetime')) return 'lifetime';
                
                return 'monthly'; // default
            }
            
            setupPriceVisibilityTracking(tableElement: any) {
                // Get known prices from the template's detected prices
                const knownPrices = this.getKnownPricesFromTemplate();
                console.log('💰 Known prices to track from template:', knownPrices);
                
                // Track each price value for visibility
                knownPrices.forEach(priceData => {
                    this.trackPriceVisibility(tableElement, priceData.price, priceData.billingPeriod);
                });
            }
            
            getKnownPricesFromTemplate(): Array<{price: string, billingPeriod: string}> {
                // Get prices from the template's variation_data
                if (this.pricingTableData && this.pricingTableData.variation_data && this.pricingTableData.variation_data.detectedPrices) {
                    return this.pricingTableData.variation_data.detectedPrices;
                }
                
                // Fallback: extract prices from the DOM if template data is not available
                console.log('⚠️ No detected prices in template, falling back to DOM extraction');
                return this.getKnownPricesFromTable();
            }
            
            getKnownPricesFromTable(): Array<{price: string, billingPeriod: string}> {
                const prices: Array<{price: string, billingPeriod: string}> = [];
                
                // Extract all text content from the table
                const tableElement = this.findPricingTable();
                const allText = tableElement?.textContent || '';
                
                // Find all price patterns in the text
                const pricePattern = /\$[\d,]+(\.[\d]{2})?/g;
                const matches = allText.match(pricePattern) || [];
                
                // Remove duplicates and create price objects
                const uniquePrices = [...new Set(matches)];
                uniquePrices.forEach(price => {
                    // Determine billing period based on surrounding context
                    const billingPeriod = this.determineBillingPeriodFromContext(tableElement, price);
                    if (billingPeriod) {
                        prices.push({ price, billingPeriod });
                    }
                });
                
                return prices;
            }
            
            determineBillingPeriodFromContext(tableElement: any, price: string): string | null {
                // Find the element containing this price
                const priceElements = this.findElementsContainingText(tableElement, price);
                
                for (const element of priceElements) {
                    const text = element.textContent?.toLowerCase() || '';
                    
                    // Check for billing period indicators
                    if (text.includes('/month') || text.includes('monthly')) {
                        return 'monthly';
                    }
                    if (text.includes('/year') || text.includes('yearly') || text.includes('annual')) {
                        return 'annual';
                    }
                    if (text.includes('/quarter') || text.includes('quarterly')) {
                        return 'quarterly';
                    }
                    if (text.includes('lifetime') || text.includes('one-time')) {
                        return 'lifetime';
                    }
                }
                
                // Default to monthly if no clear indicator found
                return 'monthly';
            }
            
            findElementsContainingText(container: any, text: string): HTMLElement[] {
                const elements: HTMLElement[] = [];
                const walker = document.createTreeWalker(
                    container,
                    NodeFilter.SHOW_TEXT,
                    null
                );
                
                let node: Node | null;
                while (node = walker.nextNode()) {
                    const textContent = node.textContent;
                    if (textContent && textContent.includes(text)) {
                        const element = node.parentElement;
                        if (element) {
                            elements.push(element);
                        }
                    }
                }
                
                return elements;
            }
            
            trackPriceVisibility(tableElement: any, price: string, billingPeriod: string) {
                // Find all elements containing this specific price
                const priceElements = this.findElementsContainingText(tableElement, price);
                
                priceElements.forEach(element => {
                    // Create intersection observer for this price element
                    const observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting) {
                                console.log(`🔄 Price ${price} (${billingPeriod}) is now visible`);
                                this.trackBillingPeriodView(billingPeriod);
                            }
                        });
                    }, {
                        threshold: 0.1, // Trigger when 10% of element is visible
                        rootMargin: '0px'
                    });
                    
                    observer.observe(element);
                });
                
                // Also check initial visibility
                setTimeout(() => {
                    const isInitiallyVisible = priceElements.some(element => {
                        return element.offsetParent !== null && 
                               element.style.display !== 'none' && 
                               !element.classList.contains('hidden');
                    });
                    
                    if (isInitiallyVisible) {
                        console.log(`🔄 Price ${price} (${billingPeriod}) is initially visible`);
                        this.trackBillingPeriodView(billingPeriod);
                    }
                }, 500);
            }
            
            extractBillingPeriod(element: any) {
                const sources = [
                    element.getAttribute('data-billing-period'),
                    element.getAttribute('data-period'),
                    element.textContent?.toLowerCase(),
                    element.closest('[data-billing-period]')?.getAttribute('data-billing-period'),
                    element.closest('[data-period]')?.getAttribute('data-period'),
                    // Check surrounding text for billing period context
                    element.closest('.billing-toggle')?.textContent?.toLowerCase(),
                    element.closest('.toggle-label')?.textContent?.toLowerCase()
                ];
                for(const source of sources) {
                    if(source) {
                        const period = source.toLowerCase().trim();
                        if(period.includes('month') || period.includes('annual') || period.includes('year') || period.includes('quarter') || period.includes('lifetime') || period.includes('one-time')) {
                            return period;
                        }
                    }
                }
                return null;
            }
            
            extractTier(element: any) {
                const sources = [
                    element.getAttribute('data-tier'),
                    element.closest('[data-tier]')?.getAttribute('data-tier'),
                    element.closest('.tier')?.getAttribute('data-tier'),
                    element.closest('[class*="tier"]')?.getAttribute('data-tier')
                ];
                for(const source of sources) {
                    if(source) return source;
                }
                return null;
            }
            
            sendTrackingRequest(endpoint: any, data: any) {
                const url = this.config.trackingEndpoint + endpoint;
            console.log('📊 Tracking request:', url, data);
                fetch(url, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(data)
                }).catch(error => {
                    console.warn('Tracking request failed:', error);
                });
            }
            
            setupSessionEnd() {
                window.addEventListener('beforeunload', () => {
                    this.endSession();
                });
                window.addEventListener('pagehide', () => {
                    this.endSession();
                });
            }
            
            endSession() {
                if(!this.sessionId) return;
                this.updateSession();
                this.sendTrackingRequest('/session/end', {
                    sessionId: this.sessionId,
                    apiKey: this.config.apiKey
                });
                if(this.updateTimer) {
                    clearInterval(this.updateTimer);
                }
                if(this.heartbeatTimer) {
                    clearInterval(this.heartbeatTimer);
                }
                if(this.observer) {
                    this.observer.disconnect();
                }
            }
            
            setSessionId(sessionId: any) {
                this.sessionId = sessionId;
                this.startTracking();
            }
            
            setPricingTableData(data: any) {
                this.pricingTableData = data;
                console.log('📊 Pricing table data set for tracking:', data);
                
                // Check for debugging data from previous session
                this.checkPreviousCTAClick();
            }
            
            checkPreviousCTAClick() {
                const lastClickData = sessionStorage.getItem('last_cta_click');
                if (lastClickData) {
                    try {
                        const clickData = JSON.parse(lastClickData);
                        console.log('🔍 Previous CTA click data found:', clickData);
                        
                        // Clear the data after logging
                        sessionStorage.removeItem('last_cta_click');
                    } catch (error) {
                        console.warn('Error parsing previous click data:', error);
                    }
                }
            }
            

            
            destroy() {
                this.endSession();
                this.isTracking = false;
            }
        }
        
        // Initialize tracker
        const tracker = new PricingTableTracker(config);
        
        // Fetch and display pricing table
        async function loadPricingTable() {
            try {
                console.log('🚀 Loading pricing table from:', config.lambdaEndpoint);
                
                const response = await fetch(config.lambdaEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        apiKey: config.apiKey,
                        // Removed hardcoded pricingTableId - API will automatically find user's active table
                    })
                });
                
                console.log('📡 Response status:', response.status);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch pricing table: ${response.status} ${response.statusText}`);
                }
                
                const data = await response.json();
                console.log('✅ Pricing table data received:', data);
                
                // Set session ID and pricing data for tracking
                const pricingData = data.data || data;
                if (pricingData.sessionId) {
                    tracker.setSessionId(pricingData.sessionId);
                }
                
                // Set the pricing table data for conversion tracking
                tracker.setPricingTableData(pricingData);
                
                // Display the pricing table
                displayPricingTable(data);
                
                // Setup tracking for the displayed table
                setTimeout(() => {
                    tracker.setupTableEventListeners();
                }, 100);
                
            } catch (error) {
                console.error('❌ Error loading pricing table:', error);
                displayFallbackTable();
            }
        }
        
        function displayPricingTable(data: any) {
            if (!containerRef.current) return;
            
            console.log('🎨 Displaying pricing table with data:', data);
            
            // Extract the actual pricing table data from the API response
            const pricingData = data.data || data;
            
            console.log('📦 Extracted pricing data:', pricingData);
            
            // Create style element for CSS
            if (pricingData.css) {
                const style = document.createElement('style');
                style.textContent = pricingData.css;
                document.head.appendChild(style);
            }
            
            // Set HTML content
            console.log('📄 HTML content preview:', pricingData.html.substring(0, 300) + '...');
            containerRef.current.innerHTML = pricingData.html;
            
            // Check if billing toggle exists in the HTML
            setTimeout(() => {
                const billingToggle = containerRef.current?.querySelector('#billingToggle');
                console.log('🔍 Billing toggle found in container:', !!billingToggle);
                if (billingToggle) {
                    console.log('🔍 Billing toggle type:', billingToggle.tagName, (billingToggle as HTMLInputElement).type);
                }
            }, 10);
            
            // Add data attributes for tracking
            containerRef.current.setAttribute('data-pricing-table', 'true');
            containerRef.current.setAttribute('data-template-id', pricingData.templateId || '');
            containerRef.current.setAttribute('data-session-id', pricingData.sessionId || '');
            
            // Execute JavaScript if provided
            if (pricingData.js) {
                try {
                    console.log('🔧 Executing pricing table JavaScript');
                    console.log('📜 Raw JS content:', pricingData.js.substring(0, 200) + '...');
                    
                    // Create a global tracking function if it doesn't exist
                    if (typeof (window as any).trackPricingInteraction === 'undefined') {
                        (window as any).trackPricingInteraction = function(action: string, tierName: string, tierPrice: string) {
                            console.log('Pricing interaction:', { action, tierName, tierPrice });
                        };
                    }
                    
                    // Extract the JavaScript content and execute it immediately
                    const jsContent = pricingData.js;
                    
                    // Remove the DOMContentLoaded wrapper and execute directly
                    const cleanJs = jsContent.replace(/document\.addEventListener\('DOMContentLoaded',\s*function\(\)\s*{([\s\S]*?)}\);?/g, '$1');
                    
                    console.log('🧹 Cleaned JS content:', cleanJs.substring(0, 200) + '...');
                    
                    // Execute JavaScript after a small delay to ensure DOM is ready
                    setTimeout(() => {
                        try {
                            // Set up the toggle functionality directly
                            const billingToggle = document.getElementById('billingToggle');
                            const monthlyPrices = document.querySelectorAll('.monthly-price');
                            const annualPrices = document.querySelectorAll('.annual-price');
                            
                            console.log('🔍 Element check:', {
                                billingToggle: !!billingToggle,
                                monthlyPrices: monthlyPrices.length,
                                annualPrices: annualPrices.length
                            });
                            
                            if (billingToggle && monthlyPrices.length > 0 && annualPrices.length > 0) {
                                console.log('🎯 Setting up toggle functionality');
                                
                                // Remove any existing listeners
                                billingToggle.removeEventListener('change', toggleHandler);
                                
                                // Add the toggle handler
                                billingToggle.addEventListener('change', toggleHandler);
                                
                                console.log('✅ Toggle functionality set up successfully');
                            } else {
                                console.warn('⚠️ Required elements not found for toggle');
                            }
                            
                        } catch (execError) {
                            console.error('Error setting up toggle:', execError);
                        }
                    }, 200);
                    
                    // Define the toggle handler function
                    function toggleHandler() {
                        const isAnnual = (this as HTMLInputElement).checked;
                        console.log('🔄 Toggle changed:', isAnnual ? 'annual' : 'monthly');
                        
                        const monthlyPrices = document.querySelectorAll('.monthly-price');
                        const annualPrices = document.querySelectorAll('.annual-price');
                        
                        // Toggle price visibility
                        monthlyPrices.forEach(price => {
                            price.classList.toggle('hidden', isAnnual);
                        });
                        
                        annualPrices.forEach(price => {
                            price.classList.toggle('hidden', !isAnnual);
                        });
                        
                        // Animate price changes
                        const prices = document.querySelectorAll('.amount');
                        prices.forEach(price => {
                            (price as HTMLElement).style.transform = 'scale(1.1)';
                            (price as HTMLElement).style.transition = 'transform 0.3s ease';
                            
                            setTimeout(() => {
                                (price as HTMLElement).style.transform = 'scale(1)';
                            }, 300);
                        });
                        
                        // Track billing period change
                        if (tracker && tracker.trackBillingPeriodClick) {
                            tracker.trackBillingPeriodClick(isAnnual ? 'annual' : 'monthly');
                        }
                    }
                    
                } catch (error) {
                    console.warn('Error executing pricing table JavaScript:', error);
                }
            } else {
                console.warn('⚠️ No JavaScript content found in API response');
            }
            
            console.log('✅ Pricing table displayed successfully');
        }
        
        function displayFallbackTable() {
            if (!containerRef.current) return;
            
            console.log('🔄 Displaying fallback table (variation 1)');

            const pick = <T,>(options: readonly T[]): T =>
                options[Math.floor(Math.random() * options.length)] as T;
            /** 20% off full annual (monthly×12), yearly total rounded up; shown /mo is that total ÷12 rounded up. */
            const annualPerMonthShown = (monthly: number) =>
                Math.ceil(Math.ceil(monthly * 12 * 0.8) / 12);

            const starterMonthly = pick([8, 9, 11] as const);
            const professionalMonthly = pick([22, 25, 30] as const);
            const enterpriseMonthly = pick([79, 89, 95] as const);
            const starterAnnual = annualPerMonthShown(starterMonthly);
            const professionalAnnual = annualPerMonthShown(professionalMonthly);
            const enterpriseAnnual = annualPerMonthShown(enterpriseMonthly);
            
            // Add CSS for the fallback table
            const style = document.createElement('style');
            style.textContent = `/* Basic Pricing Table Styles */
.pricing-container {
    max-width: 100%;
    margin: 0 auto;
    padding: 24px 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-sizing: border-box;
}

.pricing-title {
    text-align: center;
    font-size: 2.275rem;
    font-weight: 700;
    color: #3182ce;
    line-height: 1.2;
    margin-bottom: 0.25rem;
}

.pricing-subtitle {
    text-align: center;
    font-size: 0.95rem;
    color: #ffffff;
    line-height: 1.45;
    margin-top: 0;
    margin-bottom: 20px;
}

/* Billing Toggle */
.billing-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.toggle-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #4a5568;
}

.save-badge {
    background: #48bb78;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    margin-left: 8px;
}

/* Switch Toggle */
.switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e0;
    transition: .4s;
    border-radius: 34px;
}

.slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
}

input:checked + .slider {
    background-color: #3182ce;
}

input:checked + .slider:before {
    transform: translateX(26px);
}

.pricing-table {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 24px;
    width: 100%;
}

.pricing-tier {
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 12px;
    position: relative;
    transition: all 0.3s ease;
    min-width: 0;
}

.pricing-tier:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.pricing-tier.featured {
    border-color: #3182ce;
    box-shadow: 0 4px 14px rgba(49, 130, 206, 0.15);
}

.tier-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #3182ce;
    color: white;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 0.65rem;
    font-weight: 600;
    white-space: nowrap;
}

.tier-header {
    text-align: center;
    margin-bottom: 14px;
}

.tier-name {
    font-size: 1.05rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 8px;
}

.tier-price {
    position: relative;
}

.price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 4px;
    transition: opacity 0.3s ease;
}

.price.hidden {
    display: none;
}

.currency {
    font-size: 1rem;
    font-weight: 600;
    color: #1a202c;
}

.amount {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    line-height: 1;
}

.period {
    font-size: 0.8rem;
    color: #718096;
}

.tier-features {
    list-style: none;
    padding: 0;
    margin: 0 0 14px 0;
}

.tier-features li {
    padding: 8px 0;
    border-bottom: 1px solid #f7fafc;
    color: #4a5568;
    position: relative;
    padding-left: 18px;
    font-size: 0.75rem;
    line-height: 1.35;
}

.tier-features li:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #48bb78;
    font-weight: bold;
}

.tier-features li:last-child {
    border-bottom: none;
}

.tier-button {
    width: 100%;
    background: #3182ce;
    color: white;
    border: none;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s ease;
    text-align: center;
    display: block;
    box-sizing: border-box;
}

.tier-button:hover {
    background: #2c5aa0;
}

.pricing-footer {
    text-align: center;
    color: #ffffff;
    font-size: 1.3rem;
}

/* Responsive Design */
@media (max-width: 768px) {
    .pricing-table {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .pricing-title {
        font-size: 1.95rem;
    }
    
    .billing-toggle {
        flex-direction: column;
        gap: 8px;
    }
}`;
            document.head.appendChild(style);
            
            // Set the fallback HTML (variation 1 content) — prices randomized per visit
            containerRef.current.innerHTML = `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Basic Pricing Table</title>
</head>
<body>
    <div class="pricing-container">
        <h2 class="pricing-title">Refresh to see in action</h2>
        <p class="pricing-subtitle">Our system analyzes the conversion at each pricing combination to determine the most profitable pricing combo.</p>
        
        <!-- Billing Toggle -->
        <div class="billing-toggle">
            <span class="toggle-label">Monthly</span>
            <label class="switch">
                <input type="checkbox" id="billingToggle">
                <span class="slider"></span>
            </label>
            <span class="toggle-label">Annual <span class="save-badge">Save 20%</span></span>
        </div>
        
        <div class="pricing-table">
            <div class="pricing-tier">
                <div class="tier-header">
                    <h3 class="tier-name">Starter</h3>
                    <div class="tier-price">
                        <div class="price monthly-price">
                            <span class="currency">$</span>
                            <span class="amount">${starterMonthly}</span>
                            <span class="period">/month</span>
                        </div>
                        <div class="price annual-price hidden">
                            <span class="currency">$</span>
                            <span class="amount">${starterAnnual}</span>
                            <span class="period">/month</span>
                        </div>
                    </div>
                </div>
                <ul class="tier-features">
                    <li>Up to 1,000 users</li>
                    <li>Basic analytics</li>
                    <li>Email support</li>
                    <li>5GB storage</li>
                </ul>
                <a href="https://example.com/signup/starter" class="tier-button">Get Started</a>
            </div>
            
            <div class="pricing-tier featured">
                <div class="tier-badge">Most Popular</div>
                <div class="tier-header">
                    <h3 class="tier-name">Professional</h3>
                    <div class="tier-price">
                        <div class="price monthly-price">
                            <span class="currency">$</span>
                            <span class="amount">${professionalMonthly}</span>
                            <span class="period">/month</span>
                        </div>
                        <div class="price annual-price hidden">
                            <span class="currency">$</span>
                            <span class="amount">${professionalAnnual}</span>
                            <span class="period">/month</span>
                        </div>
                    </div>
                </div>
                <ul class="tier-features">
                    <li>Up to 10,000 users</li>
                    <li>Advanced analytics</li>
                    <li>Priority support</li>
                    <li>50GB storage</li>
                    <li>Custom integrations</li>
                        </ul>
                <a href="https://example.com/signup/professional" class="tier-button">Get Started</a>
            </div>
            
            <div class="pricing-tier">
                <div class="tier-header">
                    <h3 class="tier-name">Enterprise</h3>
                    <div class="tier-price">
                        <div class="price monthly-price">
                            <span class="currency">$</span>
                            <span class="amount">${enterpriseMonthly}</span>
                            <span class="period">/month</span>
                        </div>
                        <div class="price annual-price hidden">
                            <span class="currency">$</span>
                            <span class="amount">${enterpriseAnnual}</span>
                            <span class="period">/month</span>
                        </div>
                    </div>
                </div>
                <ul class="tier-features">
                    <li>Unlimited users</li>
                    <li>Custom analytics</li>
                    <li>24/7 phone support</li>
                    <li>Unlimited storage</li>
                    <li>Custom integrations</li>
                    <li>Dedicated account manager</li>
                </ul>
                <a href="https://example.com/signup/enterprise" class="tier-button">Get Started</a>
            </div>
        </div>
        
        <div class="pricing-footer">
            <p>Enterprise plans include onboarding support from our team.</p>
        </div>
    </div>

 </body></html>`;
            
            // Add JavaScript for interactivity (must not rely on DOMContentLoaded — it has already fired before React injects this HTML)
            const script = document.createElement('script');
            script.textContent = `// Basic Pricing Table JavaScript
(function() {
    function initLandingPricingTableDemo() {
    console.log('Basic pricing table loaded');
    
    // Billing toggle functionality
    const billingToggle = document.getElementById('billingToggle');
    const monthlyPrices = document.querySelectorAll('.monthly-price');
    const annualPrices = document.querySelectorAll('.annual-price');
    
    if (billingToggle) {
        billingToggle.addEventListener('change', function() {
            const isAnnual = this.checked;
            
            // Toggle price visibility
            monthlyPrices.forEach(price => {
                price.classList.toggle('hidden', isAnnual);
            });
            
            annualPrices.forEach(price => {
                price.classList.toggle('hidden', !isAnnual);
            });
            
            // Track billing preference change
            trackPricingInteraction('billing_toggle', isAnnual ? 'annual' : 'monthly', null);
            
            // Animate price changes
            animatePriceChange();
        });
    }
    
    // Add click handlers to all tier buttons
    const tierButtons = document.querySelectorAll('.tier-button');
    
    tierButtons.forEach(button => {
        button.addEventListener('click', function(e) {
            e.preventDefault();
            
            const tier = this.closest('.pricing-tier');
            const tierName = tier.querySelector('.tier-name').textContent;
            const isAnnual = billingToggle ? billingToggle.checked : false;
            
            // Get the currently visible price
            const visiblePrice = isAnnual ? 
                tier.querySelector('.annual-price .amount') : 
                tier.querySelector('.monthly-price .amount');
            const tierPrice = visiblePrice ? visiblePrice.textContent : 'N/A';
            
            const billingText = isAnnual ? 'annual billing' : 'monthly billing';
            console.log(\`Selected tier: \${tierName} at $\${tierPrice}/month with \${billingText}\`);
            
            // Show a simple alert for demo purposes
            alert(\`You selected the \${tierName} plan for $\${tierPrice}/month with \${billingText}!\`);
            
            // Track button click
            trackPricingInteraction('cta_click', tierName, tierPrice);
        });
    });
    
    // Add hover effects for better UX
    const pricingTiers = document.querySelectorAll('.pricing-tier');
    
    pricingTiers.forEach(tier => {
        tier.addEventListener('mouseenter', function() {
            this.style.transform = this.classList.contains('featured') ? 
                'scale(1.05) translateY(-8px)' : 
                'translateY(-8px)';
        });
        
        tier.addEventListener('mouseleave', function() {
            if (this.classList.contains('featured')) {
                this.style.transform = 'scale(1.05)';
            } else {
                this.style.transform = 'translateY(0)';
            }
        });
    });
    
    // Add smooth scrolling for any anchor links
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    anchorLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            e.preventDefault();
            const targetId = this.getAttribute('href').substring(1);
            const targetElement = document.getElementById(targetId);
            
            if (targetElement) {
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    });
    
    // Price animation on toggle
    function animatePriceChange() {
        const prices = document.querySelectorAll('.amount');
        prices.forEach(price => {
            price.style.transform = 'scale(1.1)';
            price.style.transition = 'transform 0.3s ease';
            
            setTimeout(() => {
                price.style.transform = 'scale(1)';
            }, 300);
        });
    }
    
    // Track pricing table interactions (for analytics)
    function trackPricingInteraction(action, tierName, tierPrice) {
        const interactionData = {
            action: action,
            tier: tierName,
            price: tierPrice,
            billing: billingToggle ? (billingToggle.checked ? 'annual' : 'monthly') : 'monthly',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
        };
        
        console.log('Pricing interaction:', interactionData);
        
        // In a real application, this would send data to an analytics service
        // Example: analytics.track('pricing_interaction', interactionData);
    }
    
    // Expose tracking function globally for potential external use
    window.trackPricingInteraction = trackPricingInteraction;
    
    // Add keyboard navigation support
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Tab') {
            // Add focus styles for accessibility
            const focusedElement = document.activeElement;
            if (focusedElement && (focusedElement.classList.contains('tier-button') || 
                                 focusedElement.classList.contains('switch'))) {
                focusedElement.style.outline = '2px solid #3182ce';
                focusedElement.style.outlineOffset = '2px';
            }
        }
    });
    
    // Remove focus styles when clicking elsewhere
    document.addEventListener('click', function(e) {
        if (!e.target.classList.contains('tier-button') && 
            !e.target.classList.contains('switch')) {
            const buttons = document.querySelectorAll('.tier-button, .switch');
            buttons.forEach(button => {
                button.style.outline = '';
                button.style.outlineOffset = '';
            });
        }
    });
    
    // Initialize tooltips for save badge
    const saveBadge = document.querySelector('.save-badge');
    if (saveBadge) {
        saveBadge.title = 'Save 20% with annual billing';
    }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initLandingPricingTableDemo);
    } else {
        initLandingPricingTableDemo();
    }
})();`;
            document.head.appendChild(script);
        }
        
        loadPricingTable();
        
        // Cleanup on unmount
        return () => {
            if (tracker) {
                tracker.destroy();
            }
        };
    }, []);
    
    return (
        <div className='mx-auto mt-32 max-w-7xl px-6 lg:px-8'>
            <div className='mx-auto max-w-4xl text-center'>
                <h2 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white'>
                    See An Example Pricing Table in Action
                </h2>
                <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-white'>
                    This is an example pricing table loaded from our system, showing how your pricing will look after our AI processes it for A/B testing.
                </p>
            </div>
            
            <div className='mx-auto mt-16 w-full max-w-4xl min-w-0'>
        <div 
            ref={containerRef}
            id="pricing-table-container"
            className='w-full min-w-0'
            style={{ minHeight: '200px', margin: '20px 0' }}
        >
            <div style={{ textAlign: 'center', padding: '20px' }}>
                Loading pricing table...
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingTable;